<!--
  SidebarLayout.vue - Mezmo Sidebar Framework

  EXTRACTED FROM MEZMO: /projects/logdna-web/client/components/frame/sidebar/SidebarLayout.vue
  Original Lines: ~400 lines
  Adapted Lines: ~150 lines (Pinia/Router/i18n/feature flags removed)

  MODIFICATIONS FROM MEZMO:
    - Removed: Pinia stores (useUIStore, useAccountStore, useRootStore)
    - Removed: Vue Router ($router, router-link)
    - Removed: i18n ($t for translations)
    - Removed: Feature flags (conditional rendering)
    - Removed: Tab folding/expanding logic (~240 lines, lines 111-147, 312-349 in original)
    - Removed: Dropdown menu for folded tabs
    - Simplified: Single "Investigations" tab (no tab switching)

  REPLACEMENT PATTERN:
    - Pinia → useSidebar composable (module-level state)
    - Router → Plain anchor tags (<a href="/">)
    - i18n → Static English strings
    - Multi-tab system → Single always-active tab

  SLOTS PROVIDED:
    - #branding - Logo/branding area (Branding.vue)
    - #toolbar - Toolbar shortcuts area (ToolShortcuts.vue)
    - #tabs - Tab icon and title (elIcon + text)
    - #tab-content - Tab content panel (InvestigationsTabContent.vue)

  STATE MANAGEMENT:
    - sidebarHidden - From useSidebar composable (shared module-level state)
    - toggleSidebar - From useSidebar composable (persists to localStorage)

  ACCESSIBILITY:
    - aria-label on collapse/expand button
    - Tab role on sidebar tab
    - role="navigation" on sidebar container
    - Keyboard navigation support (already in useSidebar)
-->
<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<template>
  <div
    id="sidebar"
    data-bs-theme="dark"
    data-testid="sidebar-layout"
    class="content-sidebar"
    :class="{ 'hidden-sidebar': sidebarHidden }"
  >
    <!-- Branding slot -->
    <div class="left-nav">
      <slot name="branding" />
    </div>

    <!-- Toolbar slot -->
    <slot name="toolbar" />

    <!-- Toggle Sidebar btn -->
    <button
      type="button"
      class="btn btn-sm collapse-btn px-2"
      :class="{ 'collapse-btn--flipped': !sidebarHidden }"
      :aria-label="sidebarHidden ? 'Expand sidebar' : 'Collapse sidebar'"
      :title="'Toggle Sidebar'"
      @click.prevent="toggleSidebar"
      @keydown.enter.prevent="toggleSidebar"
    >
      <svg
        class="collapse-btn__icon"
        viewBox="0 0 15 12"
        fill="currentColor"
        width="15"
        height="12"
      >
        <path
          fill-rule="nonzero"
          d="M9.131 0L7.886 1.273 11.68 5.15H0V6.85h11.68l-3.794 3.878L9.13 12 15 6z"
        />
        <path
          fill-rule="nonzero"
          opacity=".6"
          d="M0 1.2h6.923V3H0zM0 9h6.923v1.8H0z"
        />
      </svg>
    </button>

    <!-- Single Tab - Investigations -->
    <div
      id="sidebar-nav"
      class="sidebar-nav dark-contrast"
    >
      <div :class="NAV_WRAPPER_CLASS" style="display: none;">
        <ul class="nav-tabs" role="tablist">
          <li role="presentation">
            <a
              class="nav-link active"
              role="tab"
              aria-selected="true"
              data-testid="investigations-tab"
              aria-label="Investigations"
            >
              <div class="d-flex flex-column align-items-center px-3 py-2">
                <slot name="tabs" />
              </div>
            </a>
          </li>
        </ul>
      </div>
      <div class="tab-content">
        <div class="tab-pane active show" role="tabpanel">
          <transition name="fade">
            <div v-show="!sidebarHidden">
              <slot name="tab-content" />
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useSidebar } from '@/composables/useSidebar'

const NAV_WRAPPER_CLASS = 'nav-pane-toggle'

export default {
  setup() {
    const { sidebarHidden, toggleSidebar } = useSidebar()

    return {
      NAV_WRAPPER_CLASS,
      sidebarHidden,
      toggleSidebar
    }
  }
}
</script>

<style lang="scss">
// Mezmo SCSS variables are auto-imported via vite.config.js additionalData

.content-sidebar {
  position: fixed;
  top: 0;
  bottom: 0;
  z-index: 1000; // $zIndex--sidebar
  width: 264px; // $sidebarWidth
  min-height: 100px;
  color: #dce0e3; // $lightTextColor
  background-color: $grey-30;
  transition: width 200ms ease; // $sidebarAnimDuration
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  @media print {
    display: none;
  }
}

.hidden-sidebar {
  width: 70px; // $leftNavWidth (sized to match Mezmo left nav tabs)

  .ape-scroll-bar {
    display: none;
  }
}

// Sidebar animation
.fade-enter-active,
.fade-leave-active {
  transition: opacity $sidebarAnimDuration ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.left-nav {
  position: absolute;
  top: 0;
  left: 0;
  width: 70px; // $leftNavWidth (sized to match Mezmo left nav tabs)
  height: 100%;
  overflow: hidden;
  background: #1e2022; // $darkBackground
}

.tab-pane-divider {
  width: 48px;
  height: 1px;
  margin-left: 4px;
  border-top: 1px solid $grey-40;
  cursor: default !important;
}

.sidebar-nav {
  position: relative;
  height: 100%;

  > .tab-content {
    width: 194px; // $sidebarNavWidth (264 - 70, balanced with Mezmo proportions)
    height: 100%;
    margin-left: 70px; // $leftNavWidth (sized to match Mezmo)
    padding-right: 0;
    padding-left: 0;
    background-color: $grey-25;
    border-right: 1px solid $grey-35;
    border-left: 1px solid $grey-35;

    > .tab-pane {
      height: 100%;
      padding-top: 56px; // $headerHeight
      padding-bottom: 64px; // $sidebarFooterHeight
    }
  }

  .hidden-sidebar & > .tab-content {
    visibility: hidden;
  }

  .tooltip-inner {
    max-width: none;
    white-space: nowrap;
    text-align: left;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}

.nav-pane-toggle {
  position: absolute;
  z-index: 1;
  margin-top: 71px; // $headerHeight + 15px (56 + 15)

  > .nav-tabs {
    display: block;
    border-bottom: none;

    > li {
      position: relative;
      float: none;
      width: 70px; // $leftNavWidth (sized to match Mezmo)

      // enable this to have a margin around the hover state
      // width: $leftNavWidth - 8px;
      // margin-left: 4px;

      .tab-pane-title {
        margin-top: 4px;
        color: $grey-80;
        font-size: 11px;
        white-space: nowrap;
      }

      .tab-pane-icon {
        color: $grey-80;
      }

      #foldedButton {
        button {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 25px;
          background-color: transparent;
          border: 0;

          &:focus {
            outline: none;
            box-shadow: none;
          }
        }

        .dropdown-menu {
          margin: 0;
          padding: 0 3px 0 0;
          background-color: $grey-35;
          border: none;
          border-radius: 0;

          li {
            padding: 2px;

            .dropdown-item {
              margin: 0 6px 0 3px;
              padding: 3px 5px;
              color: #fff;
              border-radius: 4px;

              &:hover {
                background-color: $grey-30;
              }

              &::before {
                display: none !important;
              }
            }

            &.active {
              position: relative;

              &::before {
                position: absolute;
                top: 5px;
                left: 0;
                width: 3px;
                height: 24px;
                background-color: $primaryColor;
                content: "";
              }
            }
          }
        }
      }

      &.disabled {
        opacity: 0.6;

        a:not(.tab-pane-divider) {
          display: inline-block;
          text-decoration: none;
          cursor: not-allowed;
          pointer-events: auto;
        }
      }

      > a.nav-link {
        width: 100%;
        padding: 0;
        text-align: center;
        border: none;

        svg {
          pointer-events: none;
        }

        &:not(.disabled).active,
        &:not(.disabled):active,
        &:not(.disabled):focus,
        &:not(.disabled):hover {
          background: $grey-35;
          border-radius: 0;

          .tab-pane-icon {
            color: $grey-95 !important;
            background: transparent;
            cursor: pointer;
          }

          .tab-pane-title {
            color: $grey-95 !important;
            white-space: nowrap;
          }
        }

        &:focus {
          outline: 0;
        }
      }
    }
  }
}

.sidebar-pane {
  position: relative;
  height: 100%;
}

.sidebar-header {
  position: absolute;
  top: -56px; // $headerHeight * -1
  left: 0;
  width: 100%;
  height: 56px; // $headerHeight
  padding: 12px 15px;
}

.sidebar-title {
  position: absolute;
  top: -32px;
  left: 18px;
  width: 190px;
  color: $grey-60;
  font-weight: 600;
}

.sidebar-footer {
  position: absolute;
  bottom: 0;
  left: 70px; // $leftNavWidth (sized to match Mezmo)
  z-index: 1001; // $zIndex--sidebar + 1
  display: flex;
  flex-direction: column;
  width: 194px; // $sidebarNavWidth (balanced proportions)
  height: 64px; // $sidebarFooterHeight
}

.nav-tab-scrollable {
  position: relative;
  height: 100%;
}

.collapse-btn {
  $this: &;

  position: absolute;
  bottom: 10px;
  left: 35px; // $leftNavWidth * 0.5 (70px * 0.5 = 35px)
  z-index: 1001; // $zIndex--sidebar + 1
  color: #c6c9cc; // scale-color($lightTextColor, $lightness: -10%)
  background-color: transparent;
  transform: translate(-50%, 0);

  &:hover {
    color: #dce0e3; // $lightTextColor
    background-color: rgba(255, 255, 255, 0.1); // $white10percent
  }

  &:active,
  &:focus {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.9) inset; // $black90percent
  }

  &__icon {
    transition: transform 0.2s ease;

    #{$this}--flipped & {
      transform: rotate(180deg);
    }
  }
}
</style>
